// @flow
import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import dragIcon from '../../assets/img/drag.png';
import dragSelectedIcon from '../../assets/img/dragSelected.png';
import {Hidden} from '@material-ui/core';
import {useRef} from 'react';

import type {Node} from 'react';

type Props = {|
  item: any,
  className?: string,
  children: Node,
  isDragging: boolean,
  handleDragStart: (e: SyntheticDragEvent<HTMLDivElement>, item: any) => void,
  handleDragEnter: (e: SyntheticDragEvent<HTMLDivElement>, item: any) => void,
  dragItemId: string,
  extraContent?: Node,
  disabled?: boolean,
  isSaving?: boolean,
|};

const useStyles = props =>
  makeStyles(theme => ({
    dragItem: {
      position: 'relative',
      background: props.dragged && props.isDragging ? '#F5F0FF' : '#F9F9FB',
      boxShadow: 'inset 0px 1px 4px rgba(140, 139, 163, 0.12)',
      border:
        props.dragged && props.isDragging
          ? '1px solid rgba(123, 57, 255, 0.4)'
          : '1px solid #F9F9FB',
      borderRadius: '12px',
      padding: '12px 20px',
      borderBottomLeftRadius: props.extraContent ? '0' : '12px',
      borderBottomRightRadius: props.extraContent ? '0' : '12px',
      marginBottom: props.extraContent ? '0' : '1rem',
      flex: 1,
      [theme.breakpoints.down('sm')]: {
        padding: '12px 8px',
      },
    },
    dragContent: {
      display: 'flex',
      alignItems: 'center',
    },
    imgDrag: {
      height: '2.4rem',
    },
    imgProducto: {
      marginLeft: '1.5rem',
      width: '2.8rem',
      borderRadius: '12px',
    },
    nameProduct: {
      color: '#55556D',
      fontSize: '15px',
      marginLeft: '1.5rem',
    },
    currentPrice: {
      fontWeight: 600,
      fontSize: '18px',
    },
    containerOffer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row-reverse',
    },
    realPrice: {
      fontSize: '14px',
      fontWeight: 500,
      textDecoration: 'line-through',
      color: '#B8B8C7',
      marginRight: '1rem',
    },
    extraContent: {
      position: 'absolute',
      top: 16,
      right: 16,
    },
  }));

export default function CustomDraggable(props: Props) {
  const {
    className,
    children,
    item,
    isDragging,
    dragItemId,
    handleDragStart,
    handleDragEnter,
    extraContent,
    isSaving,
    disabled = false,
  } = props;

  const propStyles = {
    dragged: dragItemId === item.id,
    isDragging,
    extraContent: !!extraContent,
  };
  const classes = useStyles(propStyles)();
  const itemRef = useRef();

  const onStart = e => {
    if (!disabled) handleDragStart({...e, target: itemRef.current}, item);
  };

  const onEnter = e => {
    if (!disabled) handleDragEnter({...e, target: itemRef.current}, item);
  };

  return (
    <>
      <div
        ref={itemRef}
        className={`${classes.dragItem} ${className ? className : ''}`}
        style={{
          cursor: disabled ? 'default' : 'pointer',
        }}
        draggable={!isSaving && !disabled}
        onDragStart={e => onStart(e)}
        onDragEnter={isDragging ? e => onEnter(e) : null}
      >
        <div className={classes.dragContent}>
          <Hidden smDown>
            <img
              draggable={false}
              src={
                !disabled && isDragging && dragItemId === item.id
                  ? dragSelectedIcon
                  : dragIcon
              }
              className={classes.imgDrag}
              alt="drag icon"
            />
          </Hidden>
          {children}
        </div>
      </div>
      {extraContent}
    </>
  );
}

CustomDraggable.propTypes = {
  item: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
  isDragging: PropTypes.bool.isRequired,
  handleDragStart: PropTypes.func.isRequired,
  handleDragEnter: PropTypes.func.isRequired,
  dragItemId: PropTypes.string,
  extraContent: PropTypes.node,
  isSaving: PropTypes.bool,
  disabled: PropTypes.bool,
};
